/**
 * Формат, в котором тест приходит с API (wire-shape).
 * Поля именуются как на бэке — snake_case.
 *
 * Опциональные `is_pro`, `is_favorite`, `status` — см. `docs/API_TEST_ITEM.md`.
 * Пока бэк их не отдаёт, значения мокаются в слайсе-потребителе
 * (см. `resultRecommendationsSlice`).
 *
 * ВАЖНО: этот тип используется ТОЛЬКО на границе с API.
 * Внутри стора и в компонентах — `NormalizedTestItem`.
 */
export interface TestItem {
	url: string;
	name: string;
	preview_picture: string;
	id: number;

	is_pro?: boolean;
	is_favorite?: boolean;
	status?: 'passed' | 'in_progress' | null;
}

/**
 * UI-ready форма теста — именно её хранит Redux и получают компоненты.
 *
 * Нормализация (snake→camel, mapping enum'а `status`) выполняется один раз
 * в слайсе (override `setDataLoaded`) или в `createSelector` — и больше нигде.
 * Компоненты НЕ должны маппить поля у себя.
 */
export interface NormalizedTestItem {
	url: string;
	name: string;
	previewPicture: string;
	id: number;
	isPro: boolean;
	isFavorite: boolean;
	status: 'none' | 'passed' | 'inProgress';
}

/**
 * Идентификаторы фильтра «Мои тесты» ("В избранном / Пройденные / В процессе / Все").
 * Используется селекторами `myTestsSelectors` и консумерами универсального
 * `StatusFilter` (см. `components/molecules/StatusFilter`).
 *
 * `all` — не фильтрует, остальные однозначно сводятся к свойству
 * `NormalizedTestItem` (`isFavorite` / `status === 'passed'` / `status === 'inProgress'`).
 */
export type TestsFilterId = 'all' | 'favorite' | 'passed' | 'inProgress';

/**
 * Внешнее (snake_case) представление фильтра — одно на три границы:
 *   • query страницы профиля: `/profile/tests?f=in_progress`;
 *   • query API:              `?filter=in_progress` (см. `MyTestsService::FILTERS`);
 *   • ключи i18n:             `filter.my_tests.in_progress`.
 *
 * Внутри стора фильтр всегда camelCase (`inProgress`) — маппинг живёт здесь,
 * чтобы snake_case не расползался по компонентам.
 */
export const TESTS_FILTER_SLUG: Record<TestsFilterId, string> = {
    all: 'all',
    favorite: 'favorite',
    passed: 'passed',
    inProgress: 'in_progress',
};

const SLUG_TO_TESTS_FILTER = Object.fromEntries(
    Object.entries(TESTS_FILTER_SLUG).map(([id, slug]) => [slug, id]),
) as Record<string, TestsFilterId>;

/** Неизвестный/пустой slug → дефолтный `all` (в `?f=` может прилететь мусор). */
export const parseTestsFilterSlug = (slug: string | null | undefined): TestsFilterId =>
    SLUG_TO_TESTS_FILTER[slug ?? ''] ?? 'all';
